import { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  Fab, Drawer, Box, Typography, TextField, IconButton, Avatar,
  Card, CardContent, CircularProgress, Chip, Stack, Tooltip,
  useTheme,
} from '@mui/material';
import {
  SmartToy as AiIcon,
  Close as CloseIcon,
  Send as SendIcon,
  Person as UserIcon,
  Delete as ClearIcon,
  AutoAwesome as SparkleIcon,
} from '@mui/icons-material';
import { aiApi, ChatMessage } from '@/api/endpoints/ai';
import { useAuthStore } from '@/stores/authStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { TYPE } from '@/theme/typography';

interface DisplayMessage {
  role: 'user' | 'assistant';
  content: string;
  tokens?: number;
}

// Map route path to AI context
function getContextFromPath(pathname: string): string {
  if (pathname.includes('/accounting') || pathname.includes('/revenue') || pathname.includes('/expenses') || pathname.includes('/journal')) return 'accounting';
  if (pathname.includes('/hr') || pathname.includes('/employees') || pathname.includes('/onboarding') || pathname.includes('/recruitment') || pathname.includes('/leave')) return 'hr';
  if (pathname.includes('/payroll')) return 'payroll';
  if (pathname.includes('/tenants') || pathname.includes('/leases') || pathname.includes('/facility')) return 'tenants';
  if (pathname.includes('/budget')) return 'budget';
  if (pathname.includes('/group') || pathname.includes('/consolidat')) return 'group';
  return '';
}

function getContextLabel(ctx: string): string {
  if (!ctx) return 'General';
  return ctx.charAt(0).toUpperCase() + ctx.slice(1);
}

const DRAWER_WIDTH = 400;

export default function AIAssistantFab() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const { showError } = useNotificationStore();
  const theme = useTheme();
  const primaryMain = theme.palette.primary.main;
  const primaryDark = theme.palette.primary.dark;

  // Check if AI module is enabled
  const enabledModules = useAuthStore((s) => s.getEnabledModules());
  const aiEnabled = enabledModules.includes('ai');

  const context = getContextFromPath(location.pathname);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open]);

  const chatMutation = useMutation({
    mutationFn: (data: { message: string; history: ChatMessage[] }) =>
      aiApi.chat({
        message: data.message,
        conversation_history: data.history,
        context_type: context || undefined,
      }),
    onSuccess: (res) => {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: res.data.reply, tokens: res.data.tokens_used },
      ]);
    },
    onError: (err: any) => {
      showError(err.response?.data?.detail || 'AI error');
    },
  });

  if (!aiEnabled) return null;

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed) return;

    const userMsg: DisplayMessage = { role: 'user', content: trimmed };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setInput('');

    const history: ChatMessage[] = updated
      .slice(-20)
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map((m) => ({ role: m.role, content: m.content }));

    chatMutation.mutate({ message: trimmed, history: history.slice(0, -1) });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating Action Button */}
      <Tooltip title="AI Assistant" placement="left">
        <Fab
          color="primary"
          onClick={() => setOpen(true)}
          aria-label="Open AI Assistant"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: (theme) => theme.zIndex.drawer - 1,
            width: 56,
            height: 56,
            boxShadow: `0 4px 20px ${primaryMain}4D`,
            background: `linear-gradient(135deg, ${primaryDark} 0%, ${primaryMain} 100%)`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'scale(1.08)',
              boxShadow: `0 6px 28px ${primaryMain}73`,
            },
            animation: open ? 'none' : 'pulse-ring 2.5s ease-out infinite',
            '@keyframes pulse-ring': {
              '0%': { boxShadow: `0 4px 20px ${primaryMain}4D` },
              '50%': { boxShadow: `0 4px 20px ${primaryMain}4D, 0 0 0 8px ${primaryMain}1A` },
              '100%': { boxShadow: `0 4px 20px ${primaryMain}4D` },
            },
          }}
        >
          <SparkleIcon sx={{ fontSize: 28 }} />
        </Fab>
      </Tooltip>

      {/* Chat Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: DRAWER_WIDTH },
            maxWidth: '100vw',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        ModalProps={{ keepMounted: true }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            background: `linear-gradient(135deg, ${primaryDark} 0%, ${primaryMain} 100%)`,
            color: 'primary.contrastText',
          }}
        >
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 36, height: 36 }}>
            <AiIcon sx={{ fontSize: 20 }} />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={TYPE.bold}>
              AI Assistant
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              Context: {getContextLabel(context)}
            </Typography>
          </Box>
          {messages.length > 0 && (
            <Tooltip title="Clear chat">
              <IconButton size="small" aria-label="Clear chat history" onClick={() => setMessages([])} sx={{ color: 'rgba(255,255,255,0.7)' }}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton size="small" aria-label="Close AI Assistant" onClick={() => setOpen(false)} sx={{ color: 'primary.contrastText' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Messages */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {messages.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
              <AiIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1.5 }} />
              <Typography variant="body1" color="text.secondary" fontWeight={TYPE.semibold}>
                How can I help?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                Ask about your data, policies, tax rules, or anything else.
              </Typography>
              <Stack spacing={0.75}>
                {[
                  'What is my revenue this month?',
                  'How is PAYE calculated?',
                  'Show my pending approvals',
                ].map((q) => (
                  <Chip
                    key={q}
                    label={q}
                    variant="outlined"
                    size="small"
                    clickable
                    onClick={() => {
                      setInput(q);
                      inputRef.current?.focus();
                    }}
                    sx={{ justifyContent: 'flex-start' }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {messages.map((msg, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'flex-start',
                flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
              }}
            >
              <Avatar sx={{ width: 28, height: 28, bgcolor: msg.role === 'assistant' ? 'primary.main' : 'grey.400' }}>
                {msg.role === 'assistant' ? <AiIcon sx={{ fontSize: 16 }} /> : <UserIcon sx={{ fontSize: 16 }} />}
              </Avatar>
              <Card
                variant="outlined"
                sx={{
                  maxWidth: '80%',
                  bgcolor: msg.role === 'user' ? 'action.hover' : 'background.paper',
                  borderColor: msg.role === 'user' ? 'divider' : 'primary.light',
                }}
              >
                <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: TYPE.sm }}>
                    {msg.content}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          ))}

          {chatMutation.isPending && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main' }}>
                <AiIcon sx={{ fontSize: 16 }} />
              </Avatar>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">Thinking...</Typography>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {/* Input */}
        <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={1} alignItems="flex-end">
            <TextField
              fullWidth
              multiline
              maxRows={3}
              placeholder="Ask anything..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={chatMutation.isPending}
              size="small"
              inputRef={inputRef}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
            />
            <IconButton
              color="primary"
              aria-label="Send message"
              onClick={handleSend}
              disabled={!input.trim() || chatMutation.isPending}
              size="small"
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': { bgcolor: 'primary.dark' },
                '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
                width: 36,
                height: 36,
              }}
            >
              <SendIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>
        </Box>
      </Drawer>
    </>
  );
}
